import { cn } from '@/lib/utils/cn';

export interface ProgressBarProps {
  value: number;           // 0–100
  max?: number;
  label?: string;
  showPercent?: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: 'accent' | 'success' | 'warning' | 'error';
  /** Alias for `color`; also accepts 'default'. */
  variant?: 'accent' | 'success' | 'warning' | 'error' | 'default' | string;
  className?: string;
}

const colorMap = {
  accent:  'bg-[var(--color-accent-primary)]',
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warning)]',
  error:   'bg-[var(--color-error)]',
};

const sizeMap = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

export function ProgressBar({
  value,
  max = 100,
  label,
  showPercent = false,
  size = 'md',
  color,
  variant,
  className,
}: ProgressBarProps) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const resolved = (color ?? variant ?? 'accent');
  const colorKey = (resolved === 'default' ? 'accent' : resolved) as keyof typeof colorMap;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {(label || showPercent) && (
        <div className="flex items-center justify-between text-xs">
          {label   && <span className="text-[var(--color-text-secondary)]">{label}</span>}
          {showPercent && <span className="text-[var(--color-text-primary)] font-medium">{Math.round(pct)}%</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label ?? `${Math.round(pct)}% complete`}
        className={cn('w-full bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden', sizeMap[size])}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-500 ease-out progress-fill', colorMap[colorKey] ?? colorMap.accent)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
